<template>
  <Modal :open="props.open" customHeight="h-auto" hide-close>
    <template #modal-header>
      <!-- Header Section -->
      <div class="flex flex-col text-strongest">
        <div class="flex gap-2 items-center">
          <Icon name="Pencil" class="h-4 w-4 text-info" />
          <p class="font-inter text-sm text-info font-semibold">Confirm Action</p>
        </div>
        <h3 class="font-semibold text-[20px] md:text-[28px]">Change of investment</h3>
      </div>
    </template>
    <template #modal-body>
      <div v-if="changes">
        <!-- Body Content -->
        <div class="p-8 md:p-14 text-strongest flex flex-col items-center gap-3">
          <!-- Old Selection -->
          <div class="bg-container-bg rounded-xl p-4 w-full md:w-2/3">
            <div
              class="bg-container-bg-dis-canvas rounded-lg text-sm font-medium p-1 w-min whitespace-nowrap font-inter"
            >
              Old selection
            </div>
            <div class="font-medium text-lg font-inter mt-3">
              <p v-if="changes.amount" class="font-inter">
                {{ formatCurrency(changes.amount.old) }}
              </p>
              <p v-if="changes.offering" class="font-inter">
                {{ getYieldDisplay(changes.offering.old) }}
              </p>
              <p v-if="changes.is_compounded" class="font-inter">
                {{
                  changes.is_compounded.old ? "Interest paid at maturity" : "Interest paid monthly"
                }}
              </p>
            </div>
          </div>

          <!-- Arrow -->
          <svg
            class="text-success w-4 h-14"
            viewBox="0 0 16 80"
            fill="none"
            stroke="currentColor"
            stroke-width="4"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="8" y1="4" x2="8" y2="70" />
            <polyline points="2,62 8,72 14,62" />
          </svg>

          <!-- New Selection -->
          <div class="bg-container-bg rounded-xl p-4 w-full md:w-2/3 border-2 border-success">
            <div
              class="bg-green-700 text-always-light rounded-lg text-sm font-medium px-2 py-1.5 w-min whitespace-nowrap font-inter"
            >
              New selection
            </div>
            <div class="font-medium text-2xl mt-6">
              <p v-if="changes.amount" class="font-inter">
                {{ formatCurrency(changes.amount.new) }}
              </p>
              <p v-if="changes.offering" class="font-inter">
                {{ getYieldDisplay(changes.offering.new) }}
              </p>
              <p v-if="changes.is_compounded" class="font-inter">
                {{
                  changes.is_compounded.new ? "Interest paid at maturity" : "Interest paid monthly"
                }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Fallback if no changes detected -->
      <div v-else class="p-14 text-center text-strongest">
        <p>No changes detected</p>
      </div>
    </template>

    <template #modal-footer>
      <div class="flex justify-end gap-3">
        <Button @click="handleDiscard" variant="paper-primary">
          <template #label>Discard changes</template>
        </Button>
        <Button @click="handleConfirm" variant="primary-grey">
          <template #label>Confirm change</template>
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { computed } from "vue";
import { usePurchaseFlowStore } from "@/stores";
import { Modal, Icon, Button } from "@/components";

const purchaseFlowStore = usePurchaseFlowStore();

const emit = defineEmits(["close-modal", "discard-changes", "confirm-changes"]);

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
});

// Get the actual changes from the store
const changes = computed(() => {
  const result = purchaseFlowStore.getInvestmentChanges();
  // console.log("Changes computed:", result);
  return result;
});

// Build dynamic header text based on what changed
const metricsChangeText = computed(() => {
  if (!changes.value) return "investment";

  const parts = [];
  if (changes.value.amount) parts.push("amount");
  if (changes.value.offering) parts.push("interest rate and term length");
  if (changes.value.is_compounded) parts.push("payment frequency");

  if (parts.length === 0) return "investment";
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return parts.slice(0, -1).join(", ") + ", and " + parts.slice(-1);
});

function formatCurrency(value) {
  if (!value) return "$0";
  const num = typeof value === "string" ? parseInt(value.replace(/,/g, "")) : value;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(num);
}

function getYieldDisplay(offering) {
  if (!offering) return "";
  return `${offering.interest_rate || ""}% interest rate • ${offering.bond_term / 12 || ""} year term`;
}

function handleDiscard() {
  emit("close-modal");
  emit("discard-changes");
}

function handleConfirm() {
  emit("close-modal");
  emit("confirm-changes");
}
</script>
